import { createContext, createSignal, onMount, type ParentComponent, useContext } from "solid-js";
import { getRequestEvent, isServer } from "solid-js/web";
import { detectClientLanguage, detectLanguageFromHeader, getLanguageFromCookie } from "~/utils/languageDetection";
import { getLoadedLocalesVersion, getTranslation, loadLocale, type TranslationKey } from "./translations";
import type { SupportedLocale } from "./types";
import { defaultLocale, supportedLocales } from "./types";

interface I18nContextType {
  currentLanguage: () => SupportedLocale;
  /** Loads the locale if needed, then sets it. Resolves when the new locale is ready. */
  setLanguage: (lang: SupportedLocale | "auto") => Promise<void>;
  isAutoDetect: () => boolean;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextType>();

const getInitialLanguage = (): SupportedLocale => {
  if (isServer) {
    const event = getRequestEvent();
    const cookieHeader = event?.request.headers.get("cookie") || "";

    const cookieLocale = getLanguageFromCookie(cookieHeader);
    if (cookieLocale) {
      return cookieLocale;
    }

    const acceptLanguage = event?.request.headers.get("accept-language") || undefined;
    const detected = detectLanguageFromHeader(acceptLanguage);
    return supportedLocales.includes(detected) ? detected : defaultLocale;
  }
  const browserDetected = detectClientLanguage();
  return supportedLocales.includes(browserDetected) ? browserDetected : defaultLocale;
};

// Everything outside Solid's reactive graph that has to follow the interface
// language: the cookie the next request is read back from, and the language
// the document declares. The document shell is server-rendered in English
// because no catalogue is loaded there, so the declaration can only be
// corrected here, once the client knows which locale it is rendering.
const applyLocale = (locale: SupportedLocale): void => {
  if (isServer || !supportedLocales.includes(locale)) return;
  document.cookie = `PARAGLIDE_LOCALE=${locale}; path=/; max-age=34560000`;
  document.documentElement.lang = locale;
};

const storeLanguage = (value: SupportedLocale | "auto"): void => {
  try {
    localStorage.setItem("storytell-language", value);
  } catch {
    // Blocked site data keeps the choice for this page view only.
  }
};

export const I18nProvider: ParentComponent = (props) => {
  const [currentLanguage, setCurrentLanguage] = createSignal<SupportedLocale>(getInitialLanguage());
  const [isAutoMode, setIsAutoMode] = createSignal<boolean>(true);

  const setLanguage = async (lang: SupportedLocale | "auto") => {
    if (lang === "auto") {
      setIsAutoMode(true);
      const browserLanguage = detectClientLanguage();
      const languageToUse = supportedLocales.includes(browserLanguage) ? browserLanguage : defaultLocale;
      await loadLocale(languageToUse);
      setCurrentLanguage(languageToUse);
      applyLocale(languageToUse);
      if (!isServer) storeLanguage("auto");
    } else {
      setIsAutoMode(false);
      await loadLocale(lang);
      setCurrentLanguage(lang);
      applyLocale(lang);
      if (!isServer) storeLanguage(lang);
    }
  };

  // Type-safe translation function - re-runs when currentLanguage or loaded locales change
  const translate = (key: TranslationKey, params?: Record<string, string | number>): string => {
    getLoadedLocalesVersion(); // track so we re-run when a locale is lazy-loaded
    return getTranslation(currentLanguage(), key, params);
  };

  const initializeClientLanguage = (): void => {
    if (isServer) return;

    let stored: string | null = null;
    try {
      stored = localStorage.getItem("storytell-language");
    } catch {
      // Blocked site data falls back to the browser language.
    }
    const browserLanguage = detectClientLanguage();

    if (stored === null || stored === "auto") {
      setIsAutoMode(true);
      const languageToUse = supportedLocales.includes(browserLanguage) ? browserLanguage : defaultLocale;
      setCurrentLanguage(languageToUse);
      applyLocale(languageToUse);
    } else if (supportedLocales.includes(stored as SupportedLocale)) {
      setIsAutoMode(false);
      setCurrentLanguage(stored as SupportedLocale);
      applyLocale(stored as SupportedLocale);
    } else {
      setIsAutoMode(true);
      const languageToUse = supportedLocales.includes(browserLanguage) ? browserLanguage : defaultLocale;
      setCurrentLanguage(languageToUse);
      applyLocale(languageToUse);
      localStorage.setItem("storytell-language", "auto");
    }
  };

  // On client mount: init language, then load that locale so t() has strings (no flash of en)
  onMount(() => {
    if (isServer) return;
    initializeClientLanguage();
    const locale = currentLanguage();
    if (locale !== "en") {
      // Left unhandled on purpose: entry-client reloads the tab on an unhandled
      // dynamic-import rejection, which recovers a stale locale chunk after a deploy.
      void loadLocale(locale);
    }
  });

  const value: I18nContextType = {
    currentLanguage,
    setLanguage,
    isAutoDetect: isAutoMode,
    t: translate,
  };

  return <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
};

/** Fallback i18n when provider is not available (e.g. during HMR or auth route edge cases) */
const FALLBACK_I18N: I18nContextType = {
  t: (key: string, params?: Record<string, string | number>) => {
    if (params) {
      let result = key;
      for (const [paramKey, paramValue] of Object.entries(params)) {
        result = result.replace(`{${paramKey}}`, String(paramValue));
      }
      return result;
    }
    return key;
  },
  currentLanguage: () => "en",
  setLanguage: async () => {
    /* no-op: fallback context */
  },
  isAutoDetect: () => false,
};

/**
 * Safe version of useI18n that returns a fallback when I18nProvider is not available.
 * Use this in auth pages and other components that may render during HMR or before the provider tree is ready.
 */
export const useSafeI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  return context ?? FALLBACK_I18N;
};
